import { CKForm, enums } from 'console-kit';
import _ from 'lodash';
import { all, call, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { retentionService } from '../../common/Services';
import { HEADER_FORM } from '../../common/components/Experience/common/enums';
import { experienceFilter, experienceStatus, sharingFields, sharingNetworks, FORM_ID } from '../../common/enums';

import { exportEnums } from '../../common/enums';
import { minDurationEffect } from '../../common/redux';
import { getMutable } from '../../common/Utils';

import ConsoleAppActions from '../../App/redux/app-actions';

import Winner from '../winner';
import DisplayContainer from '../display-container';
import Export from '../export-task';

import ExperienceInsightsActions from '../experience-insights/experience-insights-actions';

import ExperienceActions from './experience-actions';
import ExperienceApi from './experience-api';
import ExperienceSelectors from './experience-selectors';

function* loadExperienceHandler({ payload: { id, forceReload } }) {
  const experience = yield select(ExperienceSelectors.experience);
  if (!experience.isPopulated || forceReload) {
    // We only load the experience in if it has NOT been added yet, OR in a force mode
    yield put(ExperienceActions.fetchExperience(id || experience.id));
  } else {
    yield put(ExperienceActions.experienceReloadNotNeeded());
  }
}

function* deleteExperienceHandler({ payload: { experienceId } }) {
  try {
    yield call(ExperienceApi.deleteExperience, experienceId);
    yield put(ExperienceActions.deleteExperienceSuccess(experienceId));

    // now we should forward to the experience list
    window.location.href = `${App.globalContext.serverURL}/console/experiences/overview`;
  } catch (err) {
    yield put(ExperienceActions.hideConfirmDeleteExperienceModal());
    yield put(ExperienceActions.deleteExperienceFailure(err));
  }
}

function* requestDeleteExperienceHandler({ payload: { experienceId } }) {
  try {
    yield put(ExperienceActions.showConfirmDeleteExperienceModal());

    const { confirmDeleteExperience } = yield race({
      confirmDeleteExperience: take(ExperienceActions.confirmDeleteExperience.getType()),
      abort:                   take(ExperienceActions.hideConfirmDeleteExperienceModal.getType()),
    });

    yield put(ExperienceActions.requestDeleteExperienceSuccess());

    if (confirmDeleteExperience) yield put(ExperienceActions.deleteExperience(experienceId));
  } catch (err) {
    yield put(ExperienceActions.requestDeleteExperienceFailure(err));
  }
}

function* requestResetExperienceDataHandler({ payload: { experienceId } }) {
  try {
    yield put(ExperienceActions.showResetExperienceDataModal());

    const { resetExperienceData } = yield race({
      resetExperienceData: take(ExperienceActions.resetExperienceData.getType()),
      abort:               take(ExperienceActions.hideResetExperienceDataModal.getType()),
    });

    if (resetExperienceData) {
      const experience = yield call(ExperienceApi.resetExperienceData, experienceId);
      yield put(ExperienceActions.resetExperienceDataSuccess(experience));
      yield put(ExperienceInsightsActions.fetchExperienceTotals(experienceId));
      yield put(Export.Actions.fetchLastExport(experienceId, null, exportEnums.exportTypes.PARTICIPANTS));
      yield put(Export.Actions.fetchLastExport(experienceId, null, exportEnums.exportTypes.COUPONS));
      yield put(Winner.Actions.fetch(experienceId));
    }
  } catch (err) {
    yield put(ExperienceActions.requestResetExperienceDataFailure(err));
  }
}

function* validateExternalRefHandler({ payload: { experienceMetadata, typeRef, subtypeRef, campaignId } }) {
  try {
    yield minDurationEffect(
      call(ExperienceApi.validateExternalRef, campaignId, experienceMetadata, typeRef, subtypeRef)
    );
    yield put(ExperienceActions.validateExternalRefSuccess());
  } catch (error) {
    yield put(ExperienceActions.validateExternalRefFailure(error));
  }
}

function* _validateExternalRef(experience) {
  const { id, experienceMetadata, typeRef, subtypeRef, updateExternalRefFromExperienceMetadata } = experience;
  const dirtyFields = yield select(ExperienceSelectors.dirtyFields);

  if ((dirtyFields.experienceMetadata && updateExternalRefFromExperienceMetadata) || dirtyFields.externalRef) {
    yield call(
      ExperienceApi.validateExternalRef,
      id,
      JSON.stringify({ fields: experienceMetadata.fields }),
      typeRef.id,
      subtypeRef.id
    );
  }
}

function* updateMacroExperienceSettingsHandler() {
  try {
    const _experience = yield select(ExperienceSelectors.experience);
    yield call(_validateExternalRef, _experience);
    const experience = yield call(ExperienceApi.updateExperience, _experience, {
      updateExternalRefFromExperienceMetadata: _experience.updateExternalRefFromExperienceMetadata,
    });
    yield put(ExperienceActions.updateExperienceSuccess(experience));
    yield put(ExperienceActions.updateMacroExperienceSettingsSuccess());
  } catch (error) {
    yield put(ExperienceActions.updateExperienceFailure(error));
    yield put(ExperienceActions.updateMacroExperienceSettingsFailure());
  }
}

function* updateExperienceHandler() {
  try {
    const _experience = yield select(ExperienceSelectors.experience);

    yield put(CKForm.Actions.validateForm(FORM_ID));
    const isValid = yield select(CKForm.Selectors.isFormValid, FORM_ID);
    // Only update the experience if the form is valid
    if (isValid) {
      yield call(_validateExternalRef, _experience);

      const experience = yield call(ExperienceApi.updateExperience, _experience, {
        updateExternalRefFromExperienceMetadata: _experience.updateExternalRefFromExperienceMetadata,
      });
      yield put(ExperienceActions.updateExperienceSuccess(experience));
      yield put(ExperienceActions.resetDirtyFields());
    } else yield put(ExperienceActions.updateExperienceFailure());
  } catch (error) {
    yield put(ExperienceActions.updateExperienceFailure(error));
  }
}

function* updateExperienceBackgroundHandler({ payload }) {
  const { backgroundAssetRef } = payload;
  try {
    let experience = yield select(ExperienceSelectors.experience);
    experience = yield call(
      ExperienceApi.updateExperienceBackground,
      experience.set('backgroundAssetRef', backgroundAssetRef)
    );
    yield put(ExperienceActions.updateExperienceBackgroundSuccess(experience));
  } catch (error) {
    yield put(ExperienceActions.updateExperienceBackgroundCapabilityFailure(error));
    yield put(
      ConsoleAppActions.setAlertMessage({
        messageType: enums.messageTypes.ERROR,
        header:      'Error',
        byline:      'Experience couldn\'t update',
      })
    );
  }
}

function* updateExperienceHeaderHandler({ payload: { headerConfig } }) {
  yield put(CKForm.Actions.validateForm(HEADER_FORM));
  const isValid = yield select(CKForm.Selectors.isFormValid, HEADER_FORM);
  if (isValid) {
    try {
      const experience = yield select(ExperienceSelectors.experience);

      const _headerParams = getMutable(experience.headerConfig, { deep: true });
      //in headerConfig from payload is data which needs to be added to experience.headerConfig.modules[0].data and then experience can be updated
      _headerParams.modules[0].data = headerConfig;

      const updatedExperience = yield call(
        ExperienceApi.updateExperienceHeader,
        experience.set('headerConfig', _headerParams)
      );
      yield put(ExperienceActions.updateExperienceHeaderSuccess(updatedExperience));
    } catch (error) {
      yield put(
        ConsoleAppActions.setAlertMessage({
          messageType: enums.messageTypes.ERROR,
          header:      'Error',
          byline:      'Experience couldn\'t update',
        })
      );
      yield put(ExperienceActions.updateExperienceHeaderFailure(error));
    }
  } else {
    yield put(ExperienceActions.updateExperienceHeaderFailure());
    const errors = yield select(CKForm.Selectors.getFormErrors, HEADER_FORM),
      fieldsWithErrors = _.uniq(_.flattenDeep(_.filter(_.values(errors), (value) => !_.isEmpty(value))));

    yield put(
      ConsoleAppActions.setAlertMessage({
        messageType: enums.messageTypes.WARNING,
        header:      'The form has invalid fields.',
        byline:      fieldsWithErrors.join('<br />'),
      })
    );
  }
}

function* updatePolicyConfigHandler({ payload }) {
  const { policyConfig } = payload;
  let experience = yield select(ExperienceSelectors.experience);
  experience = yield call(
    ExperienceApi.updatePolicyConfig,
    experience.merge({ policyConfigJSON: policyConfig }, { deep: true })
  );
  if (experience.error) yield put(ExperienceActions.updatePolicyConfigFailure(experience.error));
  else yield put(ExperienceActions.updatePolicyConfigSuccess(experience));
}

function* stageExperienceHandler() {
  const _experience = yield select(ExperienceSelectors.experience);
  window.open(_experience.stagingUrl);
}

function* approveExperienceHandler({ payload }) {
  const { id } = payload;
  try {
    const experience = yield call(ExperienceApi.approve, id);
    yield put(ExperienceActions.updateExperienceStatusSuccess(experience));
    yield put(ExperienceActions.transitionStateSuccess());
  } catch (error) {
    yield put(ExperienceActions.updateExperienceStatusFailure(error));
    yield put(ExperienceActions.transitionStateFailure(error));
  }
}

function* unapproveExperienceHandler({ payload }) {
  const { id } = payload;
  try {
    const experience = yield call(ExperienceApi.unapprove, id);
    yield put(ExperienceActions.updateExperienceStatusSuccess(experience));
    yield put(ExperienceActions.transitionStateSuccess());
  } catch (error) {
    yield put(ExperienceActions.updateExperienceStatusFailure(error));
    yield put(ExperienceActions.transitionStateFailure(error));
  }
}

function* publishExperienceHandler({ payload: { id, expiryDate } }) {
  try {
    // WYN-4303[Victor]:for publishing now we would like to add the expiry date so that we can publish now in an
    // expiry retention mode
    const experience = yield call(ExperienceApi.publishNow, id, expiryDate);

    yield put(ExperienceActions.updateExperienceStatusSuccess(experience));
    yield put(ExperienceActions.transitionStateSuccess());

    yield put(ExperienceInsightsActions.fetchExperienceTotals(id));
    yield put(Export.Actions.fetchLastExport(id, null, exportEnums.exportTypes.PARTICIPANTS));
    yield put(Export.Actions.fetchLastExport(id, null, exportEnums.exportTypes.COUPONS));
    yield put(Winner.Actions.fetch(id));
  } catch (error) {
    yield put(ExperienceActions.updateExperienceStatusFailure(error));
    yield put(ExperienceActions.transitionStateFailure(error));
  }
}

function* republishExperienceHandler({ payload }) {
  const { id, publishDate, expiryDate } = payload;
  try {
    const experience = yield call(ExperienceApi.republish, id, { publishDate, expiryDate });

    yield put(ExperienceActions.updateExperienceStatusSuccess(experience));
    yield put(ExperienceActions.transitionStateSuccess());
  } catch (error) {
    yield put(ExperienceActions.updateExperienceStatusFailure(error));
    yield put(ExperienceActions.transitionStateFailure(error));
  }
}

function* scheduleExperienceExpirationHandler({ payload }) {
  const { id, expiryDate } = payload;
  try {
    const experience = yield call(ExperienceApi.scheduleExpiration, id, expiryDate);
    yield put(ExperienceActions.updateExperienceStatusSuccess(experience));
    yield put(ExperienceActions.transitionStateSuccess());
  } catch (error) {
    yield put(ExperienceActions.updateExperienceStatusFailure(error));
    yield put(ExperienceActions.transitionStateFailure(error));
  }
}

function* expireExperienceHandler({ payload }) {
  const { id } = payload;

  try {
    const _experience = yield select(ExperienceSelectors.experience);

    if (retentionService.hasRetentionStrategy(_experience)) {
      yield put(ExperienceActions.showConfirmExpireExperienceModal());

      const { expireExperience } = yield race({
        expireExperience: take(ExperienceActions.expire.getType()),
        abort:            take(ExperienceActions.hideConfirmExpireExperienceModal.getType()),
      });

      if (expireExperience) {
        yield put(ExperienceActions.hideConfirmExpireExperienceModal());
        const experience = yield call(ExperienceApi.expire, id);
        yield put(DisplayContainer.Actions.expireAll());
        yield put(ExperienceActions.updateExperienceSuccess(experience));
        yield put(ExperienceActions.transitionStateSuccess());
      }
    } else {
      const experience = yield call(ExperienceApi.expire, id);
      yield put(DisplayContainer.Actions.expireAll());
      yield put(ExperienceActions.updateExperienceSuccess(experience));
      yield put(ExperienceActions.transitionStateSuccess());
    }
  } catch (error) {
    yield put(ExperienceActions.updateExperienceFailure(error));
    yield put(ExperienceActions.transitionStateFailure(error));
  }
}

function* unscheduleExperienceHandler({ payload }) {
  const { id } = payload;
  try {
    const experience = yield call(ExperienceApi.unschedule, id);
    yield put(ExperienceActions.updateExperienceStatusSuccess(experience));
  } catch (error) {
    yield put(ExperienceActions.updateExperienceStatusFailure(error));
  }
}

function* rescheduleExperienceHandler({ payload }) {
  const { id, publishDate, expiryDate } = payload;
  try {
    const experience = yield call(ExperienceApi.reschedule, id, publishDate, expiryDate);
    yield put(ExperienceActions.updateExperienceStatusSuccess(experience));
  } catch (error) {
    yield put(ExperienceActions.updateExperienceStatusFailure(error));
  }
}

function* fetchExperienceHandler({ payload }) {
  try {
    const experience = yield call(ExperienceApi.fetchExperience, payload.id);
    yield put(ExperienceActions.fetchExperienceSuccess(experience));
    yield put(ExperienceActions.fetchExperiencesSuccess([experience])); //this will also put it in the byId list, so it can be picked up later by code using byId experience selectors
  } catch (error) {
    yield put(ExperienceActions.fetchExperienceFailure(error));
  }
}

function* fetchExperiencesHandler({
  payload: {
    status = experienceStatus.ALL,
    sort = 'lastUpdated',
    order = 'desc',
    searchFilter = '',
    offset = 0,
    max = 50,
    includeSubaccountsFilter,
  },
}) {
  try {
    const { count, experiences } = yield call(
      ExperienceApi.fetchExperiences,
      experienceFilter[status],
      sort,
      order,
      searchFilter,
      offset,
      max,
      includeSubaccountsFilter
    );
    yield put(
      ExperienceActions.fetchExperiencesSuccess(experiences, count, {
        status,
        sort,
        order,
        searchFilter,
        offset,
        max,
        includeSubaccountsFilter,
      })
    );
  } catch (error) {
    yield put(ExperienceActions.fetchExperiencesFailure(error));
  }
}

function* fetchExperienceMetricsHandler({ payload: { includeSubaccounts } }) {
  const { error, ...response } = yield call(ExperienceApi.fetchExperienceMetrics, includeSubaccounts);
  if (!!error) yield put(ExperienceActions.fetchExperienceMetricsFailure(error));
  else yield put(ExperienceActions.fetchExperienceMetricsSuccess(response));
}

function* changeMetadataHandler({ payload }) {
  const { value, hasExternalRefFormula } = payload;
  const dirtyFields = yield select(ExperienceSelectors.dirtyFields);

  if (!dirtyFields.experienceMetadata && hasExternalRefFormula) {
    yield put(ExperienceActions.showConfirmUpdateMetadataModal());
    const update = yield race({
      updateExternalRef: take(ExperienceActions.updateExternalRef.getType()),
      leaveExternalRef:  take(ExperienceActions.leaveExternalRef.getType()),
    });

    if (update.updateExternalRef || update.leaveExternalRef) {
      yield put(ExperienceActions.changeExperience('experienceMetadata', value));

      if (update.updateExternalRef) {
        yield put(ExperienceActions.changeExperience('updateExternalRefFromExperienceMetadata', true));
      }

      if (update.leaveExternalRef) {
        yield put(ExperienceActions.changeExperience('updateExternalRefFromExperienceMetadata', false));
      }
    }

    yield put(ExperienceActions.hideConfirmUpdateMetadataModal());
  } else {
    yield put(ExperienceActions.changeExperience('experienceMetadata', value));
  }
}

// This will handle the side effect required when updating default sharing config values to enable or disable sharing for that network
function* checkShareEnabledHandler({ payload: { id, value, network } }) {
  const VALIDATABLE_FIELDS = [sharingFields.TITLE, sharingFields.BYLINE, sharingFields.MESSAGE];

  // if we are updating the default share data and there's no data left after this update (because users are deleting stuff), we should disable the default sharing
  if (network === sharingNetworks.DEFAULT) {
    // if the changing field is one of the validatable fields and we have set it's value to "", we should set the enabled attribute to false if it's not already
    if (_.includes(VALIDATABLE_FIELDS, id)) {
      if (value) yield put(ExperienceActions.enableShare(network));
      else yield put(ExperienceActions.disableShare(network));
    }
  }

  // now we can update the sharing config
  yield put(ExperienceActions.updateSharingDataAttribute(id, value, network));
}

export const _testing = {
  updateExperienceHandler,
  stageExperienceHandler,
};

export default function* experienceSaga() {
  yield all([
    takeLatest(ExperienceActions.deleteExperience.getType(), deleteExperienceHandler),
    takeLatest(ExperienceActions.requestDeleteExperience.getType(), requestDeleteExperienceHandler),
    takeLatest(ExperienceActions.requestResetExperienceData.getType(), requestResetExperienceDataHandler),
    takeLatest(ExperienceActions.validateExternalRef.getType(), validateExternalRefHandler),
    takeLatest(ExperienceActions.fetchExperience.getType(), fetchExperienceHandler),
    takeLatest(ExperienceActions.fetchExperiences.getType(), fetchExperiencesHandler),
    takeEvery(ExperienceActions.updateExperience.getType(), updateExperienceHandler),
    takeLatest(ExperienceActions.stageExperience.getType(), stageExperienceHandler),
    takeEvery(ExperienceActions.publish.getType(), publishExperienceHandler),
    takeEvery(ExperienceActions.republish.getType(), republishExperienceHandler),
    takeEvery(ExperienceActions.requestExpireExperience.getType(), expireExperienceHandler),
    takeEvery(ExperienceActions.approve.getType(), approveExperienceHandler),
    takeEvery(ExperienceActions.unapprove.getType(), unapproveExperienceHandler),
    takeEvery(ExperienceActions.reschedule.getType(), rescheduleExperienceHandler),
    takeEvery(ExperienceActions.unschedule.getType(), unscheduleExperienceHandler),
    takeEvery(ExperienceActions.scheduleExpiration.getType(), scheduleExperienceExpirationHandler),
    takeEvery(ExperienceActions.updateExperienceBackground.getType(), updateExperienceBackgroundHandler),
    takeEvery(ExperienceActions.updateExperienceHeader.getType(), updateExperienceHeaderHandler),
    takeEvery(ExperienceActions.updatePolicyConfig.getType(), updatePolicyConfigHandler),
    takeEvery(ExperienceActions.fetchExperienceMetrics.getType(), fetchExperienceMetricsHandler),
    takeEvery(ExperienceActions.changeMetadata.getType(), changeMetadataHandler),
    takeEvery(ExperienceActions.checkShareEnabled.getType(), checkShareEnabledHandler),
    takeEvery(ExperienceActions.loadExperience.getType(), loadExperienceHandler),
    takeEvery(ExperienceActions.updateMacroExperienceSettings.getType(), updateMacroExperienceSettingsHandler),
  ]);
}
